import type { Qmr } from '@packages/models/api';
import type { CVT, CvtSbrResponseData, CvtStatusData, SBRResponseData } from '@packages/models/api/cvt';

import { QmrStatusId } from '@packages/models/api';
import { CvtStatus } from '@packages/models/api/cvt';

import type { UploadedCvtDiagnostic } from './qmr-cvt-diagnostic.state';

/**
 * Whether the report has a CVT diagnostic section: the feature is on, and the report is an NQR with a fail code and a
 * CVT diagnostic attached or needed. Only the backend decides the last, and sends it in the same response as the fail
 * code.
 */
export function hasCvtDiagnosticSection({
    isNqrCvtDiagnosticsFeatureEnabled,
    qmr,
}: {
    isNqrCvtDiagnosticsFeatureEnabled: boolean;
    qmr: Qmr | null;
}): boolean {
    if (!isNqrCvtDiagnosticsFeatureEnabled || !qmr?.isNqr || !qmr.failCode) {
        return false;
    }

    return Boolean(qmr.cvtDiagnosticsJobId || qmr.cvtDiagnosticsRequired);
}

/**
 * Whether the report's CVT diagnostic can be changed: its section shows, and the fail code can be changed on a draft
 * or on `/edit`. Repeats the fail-code checks of `FailCodeReportSection` and the report page.
 */
export function isCvtDiagnosticEditable({
    isEditRoute,
    isNqrCvtDiagnosticsFeatureEnabled,
    qmr,
}: {
    isEditRoute: boolean;
    isNqrCvtDiagnosticsFeatureEnabled: boolean;
    qmr: Qmr | null;
}): boolean {
    if (!qmr || !hasCvtDiagnosticSection({ isNqrCvtDiagnosticsFeatureEnabled, qmr })) {
        return false;
    }

    if (!qmr.capabilities.addFailCodeToQmr && !qmr.capabilities.editFailCodeOnQmr) {
        return false;
    }

    if (qmr.qmrStatus.qmrStatusId === QmrStatusId.Draft) {
        return true;
    }

    return isEditRoute && qmr.capabilities.viewFailCodeOnQmr && qmr.capabilities.deleteFailCodeOnQmr;
}

/** Whether a CVT diagnostic completed with symptoms or with No Result */
export function isValidCvtDiagnostic({ sbrResponseData, status }: CVT | CvtStatusData): boolean {
    if (status !== CvtStatus.RECEIVED_RESPONSE_FROM_SBR) {
        return false;
    }

    if (isNoResultErrorType(sbrResponseData)) {
        return true;
    }

    if (!sbrResponseData || !('symptomTitle' in sbrResponseData)) {
        return false;
    }

    return Boolean(sbrResponseData.symptomTitle?.length);
}

export function isNoResultErrorType(
    diagnosticResult?: CvtSbrResponseData | SBRResponseData | UploadedCvtDiagnostic
): boolean {
    if (!diagnosticResult || !('errorType' in diagnosticResult)) {
        return false;
    }

    return diagnosticResult.errorType === 'NoResult';
}

/** Whether the uploaded diagnostic finished: it holds the status it finished with, or that it can't be attached. */
export function isUploadedCvtDiagnosticFinished(uploadedDiagnostic?: UploadedCvtDiagnostic): boolean {
    return Boolean(uploadedDiagnostic?.hasAttachError || uploadedDiagnostic?.status);
}

/**
 * Whether the diagnostic uploaded from the report is still processing. It stops once it finishes or once the report
 * holds its test. The report can hold the test for a moment before the poll clears the diagnostic, or keeps it with
 * its result when it's a No Result test.
 */
export function isUploadedCvtDiagnosticProcessing({
    qmr,
    uploadedDiagnostic,
}: {
    qmr: Qmr | null;
    uploadedDiagnostic?: UploadedCvtDiagnostic;
}): boolean {
    if (!uploadedDiagnostic || isUploadedCvtDiagnosticFinished(uploadedDiagnostic)) {
        return false;
    }

    return uploadedDiagnostic.diagnosticId !== qmr?.cvtDiagnosticsJobId;
}
